import React from 'react';
import { motion } from 'framer-motion';
import { MoreVertical, Calendar, Files, Folder, FolderOpen } from 'lucide-react';
import { TFileSubfoldersItem } from '@/data/types';

interface FolderGridCardProps {
  folder: TFileSubfoldersItem;
  index: number;
  onNavigate: (path: string) => void;
  onCreateFolder?: (folderName: string) => void;
  formatDate?: (dateString: string) => string;
}

const FolderGridCard: React.FC<FolderGridCardProps> = React.memo(
  ({ folder, index, onNavigate, onCreateFolder, formatDate }) => {
    const folderName = folder.directory.split('/').pop() || 'Unnamed Folder';
    const isEmpty = folder.count === 0;

    const handleFolderClick = () => {
      onNavigate(folder.directory);
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        onClick={handleFolderClick}
        className="group relative cursor-pointer rounded-2xl border-2 border-gray-100 bg-gradient-to-br from-white to-amber-50/30 p-6 shadow-sm transition-all duration-300 hover:scale-[1.02] hover:border-amber-200 hover:shadow-xl hover:shadow-amber-100/20"
      >
        {/* Empty Folder Indicator */}
        {isEmpty && (
          <div className="absolute top-3 left-3 z-10">
            <div className="rounded-lg bg-gray-100 p-1.5">
              <div className="h-3 w-3 rounded-full bg-gray-400" />
            </div>
          </div>
        )}

        {/* Folder Icon */}
        <div className="mb-5 flex items-center justify-between">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-50 to-yellow-100 shadow-sm transition-all duration-300 group-hover:from-amber-100 group-hover:to-yellow-200">
            <motion.div whileHover={{ scale: 1.1 }} transition={{ duration: 0.2 }}>
              {isEmpty ? (
                <Folder className="h-8 w-8 text-amber-600" />
              ) : (
                <FolderOpen className="h-8 w-8 text-amber-600" />
              )}
            </motion.div>
          </div>
          <div className="relative">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={(e) => {
                e.stopPropagation();
                // TODO: Add folder-specific actions
              }}
              className="rounded-xl p-2 opacity-0 transition-all duration-200 group-hover:opacity-100 hover:bg-amber-50 hover:text-amber-600"
            >
              <MoreVertical className="h-5 w-5 text-gray-400" />
            </motion.button>
          </div>
        </div>

        {/* Folder Info */}
        <div className="mb-4">
          <h3 className="mb-1 truncate text-lg font-semibold text-gray-900" title={folderName}>
            {folderName}
          </h3>
          <p className="text-sm font-medium text-gray-500">
            {folder.count === 0 ? 'Άδειος φάκελος' : `${folder.count} αρχεί${folder.count === 1 ? 'ο' : 'α'}`}
          </p>
        </div>

        {/* Folder Type Badge */}
        <div className="mb-4">
          <span className="inline-flex rounded-xl bg-gradient-to-r from-amber-100 to-yellow-100 px-3 py-1.5 text-xs font-semibold text-amber-700 shadow-sm">
            {isEmpty ? 'Άδειος Φάκελος' : 'Φάκελος'}
          </span>
        </div>

        {/* Folder Content Preview */}
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-amber-100 p-1">
              <Files className="h-3 w-3 text-amber-600" />
            </div>
            <span className="text-xs font-medium text-gray-600">
              {isEmpty ? 'Κανένα περιεχόμενο' : folder.count === 1 ? '1 στοιχείο' : `${folder.count} στοιχεία`}
            </span>
          </div>
        </div>

        {/* Folder Meta */}
        <div className="flex items-center justify-between border-t border-gray-100 pt-4">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <div className="rounded-lg bg-gray-100 p-1">
              <Calendar className="h-3 w-3" />
            </div>
            <span className="font-medium">{formatDate ? formatDate(new Date().toISOString()) : 'Πρόσφατα'}</span>
          </div>
          <div className="rounded-lg bg-amber-50 px-2 py-1 text-xs font-medium text-amber-600">Φάκελος</div>
        </div>

        {/* Hover Effects Overlay */}
        <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-amber-500/0 to-yellow-500/0 transition-all duration-300 group-hover:from-amber-500/5 group-hover:to-yellow-500/5" />

        {/* Click Ripple Effect */}
        <motion.div
          className="pointer-events-none absolute inset-0 rounded-2xl bg-amber-200/20 opacity-0"
          whileTap={{
            opacity: [0, 0.3, 0],
            scale: [0.95, 1, 0.95],
          }}
          transition={{ duration: 0.3 }}
        />
      </motion.div>
    );
  },
);

FolderGridCard.displayName = 'FolderGridCard';

export default FolderGridCard;
